/**
 * Ekip Yönetimi (Team Management) — admin-only card lived under
 * /admin/settings. Lets the business owner add/edit/remove staff and
 * tweak which app modules each staff member can use.
 *
 * Backend routes used:
 *   GET    /api/staff/permission-keys → { keys, defaults, labels }
 *   GET    /api/staff                → list staff (only role=staff scoped to business)
 *   POST   /api/staff                → create  { email, name, password, phone, permissions }
 *   PATCH  /api/staff/{id}           → update  { name?, phone?, password?, permissions? }
 *   DELETE /api/staff/{id}           → delete
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Users, Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import { isValidBusinessEmail, EMAIL_ERROR_TR } from '@/lib/email';

const blankForm = {
  email: '', name: '', phone: '', password: '',
  permissions: {},
};

export function TeamManagementCard() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [meta, setMeta] = useState({ keys: [], defaults: {}, labels: {} });
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const loadMeta = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/staff/permission-keys`);
      setMeta(data);
    } catch (e) {
      // Non-critical — fall back to a static list so the card still renders.
      setMeta({
        keys: ['appointments', 'customers', 'payments', 'reports', 'messaging', 'bulk_import'],
        defaults: { appointments: true, customers: true, payments: false, reports: false, messaging: false, bulk_import: false },
        labels: {
          appointments: 'Randevular',
          customers: 'Müşteriler / Hastalar / Sakinler',
          payments: 'Ödeme & Borç',
          reports: 'Raporlar',
          messaging: 'SMS / Mesajlaşma',
          bulk_import: 'Toplu Aktarım',
        },
      });
    }
  };

  const loadStaff = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/staff`);
      // Filter out the synthetic quick-add staff (paydaai.local) from the
      // Team page — they exist for appointment scheduling only.
      setStaff((data || []).filter((s) => !String(s.email || '').includes('@no-email.paydaai.local')));
    } catch {
      setStaff([]);
    }
  };

  useEffect(() => { loadMeta(); loadStaff(); }, []);  // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm({ ...blankForm, permissions: { ...meta.defaults } });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      email: s.email, name: s.name || '', phone: s.phone || '',
      password: '',
      permissions: { ...meta.defaults, ...(s.permissions || {}) },
    });
    setOpen(true);
  };

  const togglePerm = (key) =>
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Ad zorunlu');
    if (!editing) {
      // Create — email + password required
      if (!isValidBusinessEmail(form.email)) return toast.error(EMAIL_ERROR_TR);
      if ((form.password || '').length < 6) return toast.error('Şifre en az 6 karakter');
    } else if (form.password && form.password.length < 6) {
      return toast.error('Yeni şifre en az 6 karakter');
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, phone: form.phone, permissions: form.permissions,
        };
        if (form.password) payload.password = form.password;
        await axios.patch(`${API_URL}/api/staff/${editing.id}`, payload);
        toast.success('Personel güncellendi');
      } else {
        await axios.post(`${API_URL}/api/staff`, {
          email: form.email, name: form.name, phone: form.phone,
          password: form.password, permissions: form.permissions,
        });
        toast.success('Personel eklendi');
      }
      setOpen(false);
      await loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`${s.name} silinsin mi?`)) return;
    try {
      await axios.delete(`${API_URL}/api/staff/${s.id}`);
      toast.success('Silindi');
      await loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  return (
    <Card className="border border-[#E5E5E5]" data-testid="team-management-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
            <Users className="w-5 h-5" strokeWidth={1.5} />
            Ekip Yönetimi
          </CardTitle>
          <Button onClick={openCreate} size="sm" className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-1" data-testid="team-add-btn">
            <Plus className="w-4 h-4" /> Personel Ekle
          </Button>
        </div>
        <p className="text-xs text-[#595959] mt-1">
          İşletmeniz adına çalışacak personeli ekleyin, modül izinlerini özelleştirin.
        </p>
      </CardHeader>
      <CardContent>
        {staff.length === 0 ? (
          <div className="text-center py-6 text-sm text-[#595959]" data-testid="team-empty-state">
            Henüz personel eklenmedi.
          </div>
        ) : (
          <div className="space-y-2">
            {staff.map((s) => {
              const enabled = Object.entries(s.permissions || {}).filter(([, v]) => v).map(([k]) => meta.labels[k] || k);
              return (
                <div key={s.id} data-testid={`team-row-${s.id}`} className="flex items-center justify-between gap-3 p-3 border border-[#E5E5E5] rounded-lg bg-white">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-[#0F172A] truncate">{s.name}</p>
                    <p className="text-xs text-[#595959] truncate">{s.email}{s.phone ? ` · ${s.phone}` : ''}</p>
                    {enabled.length > 0 && (
                      <p className="text-[11px] text-[#2563EB] mt-1 truncate">
                        İzinler: {enabled.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`team-edit-${s.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(s)} data-testid={`team-delete-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Personeli Düzenle' : 'Yeni Personel Ekle'}</DialogTitle>
            <DialogDescription>
              {editing ? `${editing.email} için bilgi ve izinleri güncelleyin.` : 'Yeni personel için giriş bilgileri ve modül izinleri.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ad Soyad</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="team-phone" />
              </div>
            </div>
            <div>
              <Label>E-posta</Label>
              <Input
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ornek@isletmem.com"
                data-testid="team-email"
              />
              {editing && <p className="text-[11px] text-[#595959] mt-1">E-postayı değiştirmek için personeli silip yeniden ekleyin.</p>}
            </div>
            <div>
              <Label>{editing ? 'Yeni Şifre (boş bırakırsanız değişmez)' : 'Şifre'}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? '••••••' : 'En az 6 karakter'}
                data-testid="team-password"
              />
              {editing && (
                <p className="text-[11px] text-[#2563EB] mt-1 inline-flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Yeni bir şifre girerseniz personel bu şifre ile giriş yapacak.
                </p>
              )}
            </div>
            <div className="pt-1">
              <Label className="text-sm">Modül İzinleri</Label>
              <p className="text-[11px] text-[#595959] mb-2">İşaretli olmayan modüller bu personel için gizlenir.</p>
              <div className="grid grid-cols-1 gap-2">
                {meta.keys.map((k) => (
                  <label key={k} className="flex items-center justify-between gap-2 px-3 py-2 bg-[#F9F8F6] rounded-lg cursor-pointer">
                    <span className="text-sm text-[#0F172A]">{meta.labels[k] || k}</span>
                    <Switch
                      checked={!!form.permissions[k]}
                      onCheckedChange={() => togglePerm(k)}
                      data-testid={`team-perm-${k}`}
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)}>İptal</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="team-submit">
                {saving ? 'Kaydediliyor…' : (editing ? 'Güncelle' : 'Ekle')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default TeamManagementCard;
